import { inject, Injectable } from '@angular/core';
import {
    ApprovedPCHDurationModel,
    ConfigConstants,
    DateUtilities,
    NotificationDetailState,
    NotificationModel,
    NotificationStateUtilities,
    NotificationUtilities,
    selectCentricarePCHEnabled,
    selectCriticalityApplicableOnNotifType,
    selectEnableDurationChange,
    selectEnableRemoteWork,
    statusStateToModel,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { PaceNotificationDetailField, PaceNotificationDetailHeader, PaceStatusBadge } from '@evorait/pace-components';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { deriveStatusBadge } from '../../core/services/status-badge/status-badge.model';

/**
 * Which field a header action belongs to. The presentational header reports the whole field back,
 * and the screen switches on its id — so these are the contract between the two.
 */
export enum NOTIFICATION_DETAIL_FIELD {
    SYSTEM = 'system',
    TECHNICAL_ID = 'technicalId',
    CONTRACT = 'contract',
    CUSTOMER_PURCHASE_ORDER = 'customerPurchaseOrder',
    EQUIPMENT = 'equipment',
    MODALITY = 'modality',
    CRITICALITY = 'criticality',
    APPROVED_PCH = 'approvedPch',
}

/**
 * Defaults for a field row, so each one below only states what it actually differs in.
 *
 * @param id
 * @param overrides
 */
function field(
    id: NOTIFICATION_DETAIL_FIELD,
    overrides: Partial<PaceNotificationDetailField> = {},
): PaceNotificationDetailField {
    return {
        id,
        label: '',
        labelClass: '',
        value: '',
        additionalValue: '',
        serialNumber: '',
        valueClass: '',
        action: 'none',
        showMissingIcon: false,
        counterAriaLabel: '',
        ...overrides,
    };
}

/**
 * Turns the notification store's {@link NotificationDetailState} into the fully rendered
 * {@link PaceNotificationDetailHeader} the presentational header consumes: dates formatted, labels
 * translated, and the config-, client- and value-help-dependent fields resolved.
 *
 * This is where the view concerns the store slice deliberately leaves out are added — the same
 * split {@link DashboardNotificationCardFactory} follows for the dashboard card.
 *
 * The `notification` argument is the store-built `NotificationModel`: the criticality description,
 * the internal-info counter and the approved-PCH times are all derived by logic that only exists on
 * that model, so the header reads them from there rather than reproducing it.
 */
@Injectable({ providedIn: 'root' })
export class NotificationDetailsHeaderFactory {
    private readonly log: LogInterface = inject(LogInstanceToken);
    private readonly translate = inject(TranslateService);
    private readonly notificationUtilities = inject(NotificationUtilities);
    private readonly store = inject(Store);

    private readonly enableDurationChange = this.store.selectSignal(selectEnableDurationChange);
    private readonly enableRemoteWork = this.store.selectSignal(selectEnableRemoteWork);
    private readonly centricarePCHEnabled = this.store.selectSignal(selectCentricarePCHEnabled);

    public create(state: NotificationDetailState, notification: NotificationModel): PaceNotificationDetailHeader {
        if (!state.hasMainEquipment) {
            this.log.error(
                `NotificationDetailsHeaderFactory: No Main Equipment available for notification ${NotificationStateUtilities.notificationIdToString(state.notificationId)}`,
            );
        }

        return {
            weekday: DateUtilities.formatWeekday(state.start),
            startDateTime: `${DateUtilities.formatDate(state.start)} | ${DateUtilities.formatTime(state.start)}`,
            endDateTime: this.getEndDateTime(state),
            isMultiDay: state.isMultiDay,
            canChangeDuration: this.enableDurationChange() && state.hasDurationTask,
            customerName: state.customerName,
            customerAriaLabel: <string>this.translate.instant('NOTIFICATION_DETAILS_ADDRESS_INFO_TITLE'),
            notificationType: this.notificationUtilities.getNotificationTypeDescriptionFor(
                state.notificationType,
                state.notificationId.qmNum,
                state.notificationId.maNum,
            ),
            isRemoteSupport: this.enableRemoteWork() && state.userStatus.includes(ConfigConstants.USER_STATUS.REMT),
            // The clarification row has never been switched on for any client, so it stays hidden.
            // Its value is in the detail state, so enabling it is a one-line change here.
            clarifyTextHtml: '',
            // Same for the MarketMap link, which only PACE US MARS ever used.
            marketMapLabel: '',
            marketMapAriaLabel: <string>this.translate.instant('NOTIFICATION_DETAILS_MARKET_MAP_LABEL'),
            userStatus: state.userStatus,
            badge: this.getBadge(state),
            fields: this.getFields(state, notification),
        };
    }

    private getBadge(state: NotificationDetailState): PaceStatusBadge {
        const badgeInfo = deriveStatusBadge({
            statusCode: state.status.statusCode,
            isCancelled: state.isCancelled,
            start: state.start,
        });

        return {
            // badgeInfo is undefined for notifications in CRM status, which carry no badge
            text: badgeInfo ? <string>this.translate.instant(`NOTIFICATION_STATUS_BADGE_${badgeInfo.badge}_LABEL`) : '',
            styleClass: badgeInfo ? badgeInfo.badge.toLowerCase() : '',
            isEscalated: state.isEscalated,
            isCancelled: state.isCancelled.length > 0,
            isUnread: state.isUnread,
        };
    }

    /**
     * A single-day notification shows only its end time; a multi-day one repeats weekday and date.
     *
     * @param state the notification's detail state
     * @returns the formatted end of the notification
     */
    private getEndDateTime(state: NotificationDetailState): string {
        if (!state.isMultiDay) {
            return ` - ${DateUtilities.formatTime(state.end)}`;
        }
        return `${DateUtilities.formatWeekday(state.end)} ${DateUtilities.formatDate(state.end)} | ${DateUtilities.formatTime(state.end)}`;
    }

    /**
     * The rows of the header's detail column, in the order they are rendered. A row whose
     * precondition does not hold is left out rather than rendered empty.
     *
     * @param state the notification's detail state
     * @param notification the store-built model, for the fields only it can derive
     * @returns the rows to render
     */
    private getFields(state: NotificationDetailState, notification: NotificationModel): PaceNotificationDetailField[] {
        const fields: PaceNotificationDetailField[] = [];

        const productName = state.mainEquipment?.equiShortDesc ?? '';
        if (productName) {
            fields.push(
                field(NOTIFICATION_DETAIL_FIELD.SYSTEM, {
                    label: <string>this.translate.instant('NOTIFICATION_DETAILS_HEADER_SYSTEM_LABEL'),
                    value: productName,
                    // Internal info is a PACE US feature; elsewhere the counter stays hidden.
                    counter: this.getInternalInfoCount(notification),
                    counterAriaLabel: <string>this.translate.instant('INTERNAL_INFO_TITLE'),
                }),
            );
        }

        const technicalId = state.mainEquipment?.tidnr ?? '';
        if (technicalId) {
            fields.push(
                field(NOTIFICATION_DETAIL_FIELD.TECHNICAL_ID, {
                    label: <string>this.translate.instant('NOTIFICATION_DETAILS_HEADER_TID_LABEL'),
                    value: technicalId,
                    serialNumber: state.mainEquipment?.sernr ?? '',
                    action: state.hasObjectInformation ? 'info' : 'none',
                }),
            );
        }

        fields.push(
            field(NOTIFICATION_DETAIL_FIELD.CONTRACT, {
                label: <string>this.translate.instant('NOTIFICATION_DETAILS_HEADER_CONTRACT_LABEL'),
                value: state.mainEquipmentContractType,
                showMissingIcon: state.mainEquipmentContractType.length === 0,
                action: state.hasContractSections ? 'info' : 'none',
            }),
        );

        if (state.customerPurchaseOrderNumber) {
            fields.push(
                field(NOTIFICATION_DETAIL_FIELD.CUSTOMER_PURCHASE_ORDER, {
                    label: <string>this.translate.instant('NOTIFICATION_DETAILS_HEADER_AKZ_LABEL'),
                    value: state.customerPurchaseOrderNumber,
                }),
            );
        }

        // The equipment-number and modality rows have never been switched on for any client, so they
        // stay out. Their values are already in the detail state, so enabling them is a one-line
        // change here.

        if (this.store.selectSignal(selectCriticalityApplicableOnNotifType(state.notificationType))()) {
            fields.push(
                field(NOTIFICATION_DETAIL_FIELD.CRITICALITY, {
                    label: <string>this.translate.instant('NOTIFICATION_DETAILS_HEADER_CRITICALITY'),
                    value: this.notificationUtilities.getNotificationStatusInArrivalDescription(notification),
                    action: statusStateToModel(state.status).canChangeCriticality() ? 'edit' : 'none',
                }),
            );
        }

        const approvedPch = this.getApprovedPchField(notification);
        if (approvedPch) {
            fields.push(approvedPch);
        }

        return fields;
    }

    /**
     * The approved-PCH row, shown for a CentriCare contract when the feature is enabled. Its value
     * is either the approved window or the warning that there is none.
     *
     * @param notification the store-built model the PCH duration is derived from
     * @returns the row, or `undefined` when the notification has no approved PCH row at all
     */
    private getApprovedPchField(notification: NotificationModel): PaceNotificationDetailField | undefined {
        if (!this.centricarePCHEnabled() || !notification.isCentriCareContract()) {
            return undefined;
        }
        const model = new ApprovedPCHDurationModel(notification);
        const label = <string>this.translate.instant('NOTIFICATION_DETAILS_HEADER_APPROVED_PCH');

        if (model.noPCHAvailable) {
            return field(NOTIFICATION_DETAIL_FIELD.APPROVED_PCH, {
                label,
                labelClass: 'pe-0',
                value: <string>this.translate.instant('NOTIFICATION_DETAILS_HEADER_NO_APPROVED_PCH'),
                valueClass: 'text-warning',
                action: 'edit',
            });
        }
        return field(NOTIFICATION_DETAIL_FIELD.APPROVED_PCH, {
            label,
            labelClass: 'pe-0',
            value: model.approvedPCHDuration.start,
            additionalValue: model.approvedPCHDuration.end,
            action: 'edit',
        });
    }

    /**
     * How many internal-info entries the notification carries: every category holding text, plus the
     * contacts as a single entry when at least one of them is valid.
     *
     * @param notification the store-built model owning the internal-info model
     * @returns the number shown in the counter badge
     */
    private getInternalInfoCount(notification: NotificationModel): number {
        const hasContact = notification.internalInfoModel.contactModel.value.some(
            (entry) => entry.model.formValidationStatus === ConfigConstants.formValid,
        )
            ? 1
            : 0;
        const categoryCount = notification.internalInfoModel.categoryModel
            .getAllCategoriesModel()
            .filter((category) => category.textLine.value.length > 0).length;

        return categoryCount + hasContact;
    }
}
